import type { BlockType } from '@unifyapps/defs/types/block';
import type { DataSourceEntity } from '@unifyapps/defs/types/dataSource';
import type { DependsOnType } from '@unifyapps/defs/types/page';
import { isEmpty } from 'lodash';
import { useCallback, useMemo } from 'react';
import { DependencyHelper } from '../../dependency/helpers/DependencyHelper';
import DataSourceHelper from '../helper/DataSourceHelper';
import DependencyFlowHelper from '../helper/DependencyFlowHelper';
import { DependencyPaths } from '../helper/DependencyPaths';
import { ComputeContextHelper } from '../hooks/computeContext/ComputeContextHelper';
import type { ComputeContextType } from '../hooks/computeContext/context/types';
import type { DependencyFlowInstanceType } from '../slices/DataSourceControllerSlice/types';
import { useGlobalAppStateStore } from '../stores/app/GlobalAppStateStore';
import {
  useDataSourceRecords,
  useGetAppDataSourceRecordStoreState,
} from '../stores/DataSourceRecordStore';
import { useGlobalStateStore } from '../stores/GlobalStateStore';

export function getIsEntitySuspendedDependencies(dataSourceIds: string[]) {
  const dsDep = dataSourceIds.map<DependsOnType>((dsId) => {
    const paths = DependencyPaths.dataSource(dsId);
    return {
      id: dsId,
      p: [paths.isLoading],
    };
  });

  // sessionStorage is now synchronous, no need to track isLoading for suspension
  // removed sessionStorage dependency since it doesn't block rendering
  return DependencyHelper.mergeDependsOn([dsDep]);
}

function useBaseIsEntitySuspended({
  dataSourceIds = [],
  context,
  getDependencyFlow,
  getDataSourceRecords,
}: {
  dataSourceIds: BlockType['dataSourceIds'];
  context: ComputeContextType;
  getDependencyFlow: () => DependencyFlowInstanceType;
  getDataSourceRecords: () => Record<string, DataSourceEntity | undefined>;
}) {
  const dataSourceRecords = getDataSourceRecords();
  const automaticDataSourceIds = useMemo(() => {
    // it is possible that we might have includes manual data source id in dataSourceIds
    return dataSourceIds.filter((id) => {
      return !DataSourceHelper.isManualDataSource(dataSourceRecords[id]);
    });
  }, [dataSourceIds, dataSourceRecords]);

  const areAllQueriesReady = automaticDataSourceIds.every((id) => {
    return DependencyFlowHelper.isDependencyFinished(getDependencyFlow(), id);
  });

  const isWaitingForRequestsToBeMade = automaticDataSourceIds.some((id) => {
    const dataSourceQueryResult = ComputeContextHelper.getDataSourceQueryResult(context, id);
    return isEmpty(dataSourceQueryResult);
  });

  const isLoadingBlockingQueries = automaticDataSourceIds.some((id) => {
    const dataSourceQueryResult = ComputeContextHelper.getDataSourceQueryResult(context, id);
    return dataSourceQueryResult?.isLoading !== false;
  });

  return {
    isSuspended: isLoadingBlockingQueries || isWaitingForRequestsToBeMade || !areAllQueriesReady,
  };
}

export function useIsEntitySuspended({
  dataSourceIds = [],
  context,
}: {
  dataSourceIds: BlockType['dataSourceIds'];
  context: ComputeContextType;
}) {
  const dsDependencyFlow = useGlobalStateStore().hooks.dataSource.useDependencyFlow();
  const appDataSourceDependencyFlow = useGlobalAppStateStore().hooks.dataSource.useDependencyFlow();
  const dataSourceRecords = useDataSourceRecords();

  const getDependencyFlow = useCallback(() => {
    return {
      ...dsDependencyFlow,
      ...appDataSourceDependencyFlow,
    };
  }, [dsDependencyFlow, appDataSourceDependencyFlow]);

  const getDataSourceRecords = useCallback(() => {
    return dataSourceRecords;
  }, [dataSourceRecords]);

  return useBaseIsEntitySuspended({
    dataSourceIds,
    context,
    getDependencyFlow,
    getDataSourceRecords,
  });
}

export type UseIsEntitySuspended = typeof useIsEntitySuspended;

export function useGlobalIsEntitySuspended({
  dataSourceIds = [],
  context,
}: {
  dataSourceIds: BlockType['dataSourceIds'];
  context: ComputeContextType;
}) {
  const { getAppDataSourceRecordStoreState } = useGetAppDataSourceRecordStoreState();
  const appDataSourceDependencyFlow = useGlobalAppStateStore().hooks.dataSource.useDependencyFlow();

  return useBaseIsEntitySuspended({
    dataSourceIds,
    context,
    getDependencyFlow: () => appDataSourceDependencyFlow,
    getDataSourceRecords: () => getAppDataSourceRecordStoreState().dataSources,
  });
}
